import classNames from 'classnames';
import React, { useContext, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { CartPricingProvider, useCartPricing } from '../../contexts/CartPricingContext';
import { useLocale } from '../../contexts/LocaleContext';
import { useStickyHeaderOffset } from '../../hooks/useStickyHeaderOffset';
import * as events from '../../lib/analytics/events';
import CheckoutContext from '../../lib/CheckoutContext';
import { getFiGrowthBook } from '../../lib/growthbook';
import { HARDWARE_FIRST_MAX_UNITS_PER_CART } from '../../lib/hardwareFirst';
import * as types from '../../types';
import ItemsInCart from '../ItemsInCart';
import HardwareFirstCartHeader from './HardwareFirstCartHeader';
import HardwareFirstCheckoutBar from './HardwareFirstCheckoutBar';
import HardwareFirstGiftToggle from './HardwareFirstGiftToggle';
import HardwareFirstOrderSummary from './HardwareFirstOrderSummary';
import HardwareFirstValueProps from './HardwareFirstValueProps';
import styles from './HardwareFirstCartLayout.module.scss';

/** Scroll target for the cart's item list, used when an upsell add appends a row above the fold. */
export const HARDWARE_FIRST_CART_ITEMS_ID = 'hardware-first-cart-items';

/** Measured when scrolling to a new row, so the row is parked above the bar rather than under it. */
export const HARDWARE_FIRST_CHECKOUT_BAR_ID = 'hardware-first-checkout-bar';

/** Reports the cart view with the arm's repriced figures; the provider mounts this only once pricing has landed. */
function HardwareFirstCartViewed() {
  const { cart } = useContext(CheckoutContext);
  const { currency } = useLocale();
  const cartPricing = useCartPricing();

  useEffect(() => {
    events.cartPage.viewed(cart.cartItems, currency, cartPricing, cart.couponCode);
    // once per mount, with the pricing snapshot the page first rendered
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}

interface HardwareFirstCartLayoutProps {
  fetchPricing: () => Promise<types.CartPricing | undefined>;
  /** At the per-cart unit cap the upsell slot carries the bulk-sales notice instead. */
  atCap: boolean;
  upsell: React.ReactNode;
  onCheckOut: (checkoutCart: types.Cart, cartPricing: types.CartPricing) => void;
}

/**
 * The hardware-first cart page: cards on a tinted ground. Source order is the mobile stack, which
 * the grid re-places into an items column and a summary rail at desktop widths.
 */
export default function HardwareFirstCartLayout({
  fetchPricing,
  atCap,
  upsell,
  onCheckOut,
}: HardwareFirstCartLayoutProps) {
  const { t } = useTranslation();
  // the gift toggle ships dark until lifecycle has a use for the flag it sets
  const giftToggleEnabled = getFiGrowthBook().getFeatureValue<boolean>('hardware-first-gift-toggle', false);
  // the total stays in front of the shopper while the items scroll. the app bar is sticky and
  // changes height with the promotion banner, so the offset is measured rather than hardcoded.
  const railRef = useRef<HTMLDivElement>(null);
  useStickyHeaderOffset(railRef, '--hf-sticky-top');
  // the rail's sticky offset flips to its bottom edge once it outgrows the viewport, which needs
  // its live height; a promo chip or the gift toggle changes it after mount
  useEffect(() => {
    const rail = railRef.current;
    if (!rail || typeof window.ResizeObserver === 'undefined') return;
    const observer = new window.ResizeObserver(() => {
      rail.style.setProperty('--hf-rail-height', `${rail.offsetHeight}px`);
    });
    observer.observe(rail);
    return () => observer.disconnect();
  }, []);

  return (
    <div className={`${styles.page} hardware-first-cart`}>
      <HardwareFirstCartHeader />
      <CartPricingProvider fetchPricing={fetchPricing}>
        <HardwareFirstCartViewed />
        <div className={styles.grid}>
          {/* the columns are `display: contents` on mobile, where `order` lays the sections out in
              the single stack the design asks for; at desktop each becomes its own stack */}
          <div className={styles.column}>
            <div className={styles.items} id={HARDWARE_FIRST_CART_ITEMS_ID}>
              <ItemsInCart allowRemoveCartItem allowRemoveCode={false} />
            </div>
            <div className={styles.upsell}>
              {atCap ? (
                <div className={styles.bulkNotice}>
                  {t('hardware_first_bulk_sales_message', { count: HARDWARE_FIRST_MAX_UNITS_PER_CART })}
                </div>
              ) : (
                upsell
              )}
            </div>
          </div>
          <div className={classNames(styles.column, styles.railColumn)} ref={railRef}>
            <div className={styles.summary}>
              <HardwareFirstOrderSummary onCheckOut={onCheckOut} />
            </div>
            {giftToggleEnabled && (
              <div className={styles.gift}>
                <HardwareFirstGiftToggle />
              </div>
            )}
            <div className={styles.valueProps}>
              <HardwareFirstValueProps />
            </div>
          </div>
        </div>
        <HardwareFirstCheckoutBar id={HARDWARE_FIRST_CHECKOUT_BAR_ID} onCheckOut={onCheckOut} />
      </CartPricingProvider>
    </div>
  );
}
